/** @file attachment.attach: flattens a source puppet into the host under `att<i>.` and records it (R1). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Attachment } from '@zilo/pose3d-format/types/puppet';
import { checkRecord } from '@zilo/pose3d-format/validate/fields/checkRecord';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { clipInverse } from '../apply/clipInverse';
import { puppetInverse } from '../apply/puppetInverse';
import type { CommandContext, PuppetSource } from '../context/commandContext';
import { orderNodes } from '../node/orderNodes';
import { checkClip } from '../puppetDoc/checkClip';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';
import type { Command } from '../types/command';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { checkAttachmentRefs } from './checkAttachmentRefs';
import { flattenPlan } from './flattenPlan';
import { mergeSource } from './mergeSource';
import { prefixOf } from './prefixOf';
import { readAttachmentFields } from './readAttachmentFields';
import { removePrefixed } from './removePrefixed';

const name = checkString(NAME_PATTERN);

/** Undo of a replace: the puppet, then every clip name the old or new attachment uses (re-import or delete). */
function replaceInverse(draft: PuppetDoc, src: PuppetSource, prefix: string): Command[] {
  const names = new Set([...Object.keys(draft.clips).filter((c) => c.startsWith(prefix)), ...Object.keys(src.clips).map((c) => prefix + c)]);
  const clips = [...names].map((c): Command => {
    const prev = draft.clips[c];
    return prev === undefined ? { name: 'clip.delete', input: { clip: c } } : clipInverse(prev);
  });
  return [...puppetInverse(draft), ...clips];
}

/** The source puppet (`NOT_FOUND`; `VALIDATION` when it has attachments itself, they do not nest). */
function sourceOf(ctx: CommandContext, source: string): PuppetSource {
  const src = ctx.puppets[source] ?? throwError(ERROR_CODES.NOT_FOUND, { source }, { path: '/input/source', hint: `sources: ${Object.keys(ctx.puppets).join(', ')}` });
  if ((src.puppet.attachments ?? []).length > 0) throwError(ERROR_CODES.VALIDATION, { source }, { path: '/input/source', hint: 'attachments do not nest' });
  return src;
}

/** A new attachment's prefix must be free: `VALIDATION` when a node, drawable, sheet or clip already uses it. */
function refuseTaken(draft: PuppetDoc, prefix: string): void {
  const p = draft.puppet;
  const names = [...Object.keys(p.nodes), ...p.drawables.map((d) => d.id), ...Object.keys(p.sheets), ...Object.keys(draft.clips)];
  const hit = names.find((n) => n.startsWith(prefix));
  if (hit !== undefined) throwError(ERROR_CODES.VALIDATION, { name: hit, prefix }, { path: '/input/id', hint: `rename ${hit} first; ${prefix} names belong to this attachment` });
}

/** The attachment's index: its own with `replace`, else the end of the list (`VALIDATION` for a taken id). */
function indexOf(list: readonly Attachment[], id: string, replace: boolean): { index: number; replacing: boolean } {
  const existing = list.findIndex((a) => a.id === id);
  if (existing >= 0 && !replace) throwError(ERROR_CODES.VALIDATION, { id }, { path: '/input/id', hint: 'pass replace: true to re-attach' });
  return existing >= 0 ? { index: existing, replacing: true } : { index: list.length, replacing: false };
}

/**
 * `attachment.attach { id, source, nodeMap, weaponGroup?, when?, driver?, replace? }` → `{ id, prefix, mergedNodes,
 * addedNodes, clips }`: `ctx.puppets[source]` (no nested attachments) enters the host with prefix `att<i>.`; `replace`
 * re-attaches over the attachment `id` at its index. Inverse: `attachment.detach`, or P plus the clips for a replace.
 */
export const attachmentAttachCommand: CommandDef<'attachment.attach'> = {
  name: 'attachment.attach',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['id', 'source', 'nodeMap', 'weaponGroup', 'when', 'driver', 'replace']);
    return compact({
      id: r.check('id', name) as string, source: r.str('source'), nodeMap: r.opt('nodeMap', (k) => r.check(k, checkRecord(name)) as Record<string, string>),
      ...readAttachmentFields(r), replace: r.opt('replace', r.bool),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const src = sourceOf(ctx, input.source);
    const list = p.attachments ?? [];
    const { index, replacing } = indexOf(list, input.id, input.replace === true);
    const prefix = prefixOf(index);
    const inverse: Command[] = replacing ? replaceInverse(draft, src, prefix) : [{ name: 'attachment.detach', input: { id: input.id } }];
    if (replacing) removePrefixed(draft, prefix);
    else refuseTaken(draft, prefix);
    const nodeMap = input.nodeMap ?? {};
    const plan = flattenPlan(p, src.puppet, nodeMap, prefix);
    const clips = mergeSource(draft, src, plan.rename, prefix, ctx);
    const record: Attachment = compact({ id: input.id, source: input.source, nodeMap: { ...nodeMap }, weaponGroup: input.weaponGroup, when: input.when, driver: input.driver });
    p.attachments = [...list.slice(0, index), record, ...list.slice(index + 1)];
    checkAttachmentRefs(p, record, '/input');
    p.nodes = orderNodes(p.nodes);
    checkPuppet(draft, ctx);
    for (const c of clips) checkClip(c, draft.clips[c] ?? throwError(ERROR_CODES.INTERNAL, { clip: c }));
    return { result: { id: input.id, prefix, mergedNodes: plan.merged, addedNodes: plan.added, clips }, warnings: [], inverse };
  },
};
